import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { WS_URL, VTE_ENABLE_SOCKET } from '../config/constants';
import { STORAGE_KEYS } from '@shared/constants';
import { useAuth } from '../contexts/AuthContext';
import { Notification } from '../services/notification.service';

interface UseNotificationSocketOptions {
  onNewNotification?: (notification: Notification) => void;
  onNotificationRead?: (notificationId: string) => void;
  onRefresh?: () => void;
}

/**
 * Hook để quản lý socket connection riêng cho notifications với namespace /notifications
 * Tạo connection riêng với authentication token giống useAttendanceSocket
 */
export const useNotificationSocket = (options: UseNotificationSocketOptions = {}) => {
  const { isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const isMountedRef = useRef(true);
  const callbacksRef = useRef(options);

  // Cập nhật callbacks ref mỗi khi options thay đổi
  useEffect(() => {
    callbacksRef.current = options;
  }, [options.onNewNotification, options.onNotificationRead, options.onRefresh]);

  useEffect(() => {
    isMountedRef.current = true;

    // Cleanup nếu không enable hoặc chưa authenticated
    if (!VTE_ENABLE_SOCKET || !isAuthenticated) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    const initSocket = async () => {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (!token) return;

        // Xử lý URL và tạo namespace
        let baseUrl = WS_URL.replace(/\/api$/, '').replace(/\/+$/, '');
        const notificationsNamespace = `${baseUrl}/notifications`;

        const newSocket = io(notificationsNamespace, {
          auth: { token },
          transports: ['websocket'],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 2000,
          timeout: 5000,
          autoConnect: true,
        });

        // Connection handlers
        newSocket.on('connect', () => {
          if (isMountedRef.current) {
            setIsConnected(true);
            newSocket.emit('notification:subscribe');
          }
        });

        newSocket.on('disconnect', () => {
          if (isMountedRef.current) setIsConnected(false);
        });

        newSocket.on('connect_error', () => {
          if (isMountedRef.current) setIsConnected(false);
        });

        // Event listeners
        newSocket.on('notification:new', (data: Notification) => {
          callbacksRef.current.onNewNotification?.(data);
        });

        newSocket.on('notification:read', (notificationId: string) => {
          callbacksRef.current.onNotificationRead?.(notificationId);
        });

        newSocket.on('notification:refresh', () => {
          callbacksRef.current.onRefresh?.();
        });

        socketRef.current = newSocket;
      } catch (error) {
        if (isMountedRef.current) setIsConnected(false);
      }
    };

    initSocket();

    return () => {
      isMountedRef.current = false;
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated]);

  return {
    isConnected,
    socket: socketRef.current,
  };
};
